import type { Role } from "../../types";

export interface AdminUser {
  id: string;
  email: string;
  name?: string;
  role: Role;
  organizationId?: string;
  isActive: boolean;
  passwordSetAt?: string;
  hasPassword: boolean;
  lastSeenAt?: string;
}

export interface SetupLinkResult {
  setupLink: string;
  expiresAt: string;
  emailSent?: boolean;
  emailError?: string;
}

async function parseJson<T>(response: Response): Promise<T> {
  return (await response.json()) as T;
}

async function assertOk(response: Response): Promise<void> {
  if (response.ok) return;
  const data = await response.json().catch(() => undefined) as { error?: string } | undefined;
  throw new Error(data?.error ?? "Die Anfrage konnte nicht verarbeitet werden.");
}

export async function fetchAdminUsers(): Promise<AdminUser[]> {
  const response = await fetch("/api/admin/users", { credentials: "include" });
  await assertOk(response);
  const data = await parseJson<{ users: AdminUser[] }>(response);
  return data.users;
}

export async function updateAdminUserEmail(userId: string, email: string): Promise<AdminUser> {
  const response = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/email`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email })
  });
  await assertOk(response);
  const data = await parseJson<{ user: AdminUser }>(response);
  return data.user;
}

export async function generatePasswordSetupLink(userId: string): Promise<SetupLinkResult> {
  const response = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/password-setup-link`, {
    method: "POST",
    credentials: "include"
  });
  await assertOk(response);
  return parseJson<SetupLinkResult>(response);
}

export async function sendPasswordSetupLink(userId: string): Promise<SetupLinkResult> {
  const response = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/password-setup-link/send`, {
    method: "POST",
    credentials: "include"
  });
  await assertOk(response);
  return parseJson<SetupLinkResult>(response);
}

export async function setPasswordWithSetupToken(token: string, password: string): Promise<{ ok: boolean; message?: string }> {
  const response = await fetch("/api/auth/setup-password", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ token, password })
  });
  const data = await response.json().catch(() => ({})) as { ok?: boolean; message?: string; error?: string };
  if (!response.ok) return { ok: false, message: data.message ?? data.error ?? "Passwort konnte nicht gesetzt werden." };
  return { ok: Boolean(data.ok), message: data.message };
}
